import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

function StatusBadge({ status }) {
  const styles = status === 'open'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
      {status === 'open' ? 'Open' : 'Closed'}
    </span>
  );
}

function MessageBubble({ message }) {
  const fromStaff = message.from_staff;
  return (
    <div className={`flex ${fromStaff ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        fromStaff ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <p className={`text-xs font-semibold ${fromStaff ? 'text-indigo-700' : 'text-gray-700'}`}>
            {message.author_name}
          </p>
          <span className="text-xs text-gray-400">·</span>
          <p className="text-xs text-gray-400">{message.created_at}</p>
        </div>
        <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{message.body}</p>
      </div>
    </div>
  );
}

export default function SupportShow({ ticket }) {
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReply = (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSubmitting(true);
    router.post(`/support/${ticket.id}/messages`, { message: { body: reply.trim() } }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => setReply(''),
    });
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link href="/support" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700">
            ← Back to support
          </Link>
          <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 break-words">{ticket.subject}</h1>
              <p className="text-xs text-gray-500 mt-1">Opened {ticket.created_at}</p>
            </div>
            <StatusBadge status={ticket.status} />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          {ticket.messages.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No messages yet.</p>
          ) : (
            ticket.messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
        </div>

        {ticket.status === 'open' ? (
          <form onSubmit={handleReply} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-3">
            <label htmlFor="reply" className="block text-sm font-semibold text-gray-700">
              Add a reply
            </label>
            <textarea
              id="reply"
              value={reply}
              rows={4}
              maxLength={10000}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Type your reply…"
              className="block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition resize-y"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !reply.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
              >
                {submitting ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-sm text-gray-600 text-center">
            This ticket is closed. <Link href="/support/new" className="font-semibold text-indigo-600 hover:text-indigo-700">Open a new request</Link> if you need more help.
          </div>
        )}
      </div>
    </Layout>
  );
}
